import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Search, X } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { amount, compactMoney, dateLabel, money, shortMonth, useBooks } from "@/lib/books";
import { Input } from "@/components/ui/input";
import { DataTable, Loaded, NotConnected, Pager, Tabs } from "@/components/books/ui";
import { LedgerStatement, usePeriod } from "@/components/books/Statement";
import { SERIES_1 } from "@/components/books/charts";

const COPY = {
  customer: { title: "Customers", one: "customer", owed: "Receivables", path: "/customers", base: "Sales",
              chart: "Income", sections: [["Sales", "Invoices"], ["Receipt", "Customer payments"], ["Credit Note", "Credit notes"],
                                         ["Journal", "Journals"], ["Debit Note", "Debit notes"], ["Payment", "Payments made"]] },
  vendor: { title: "Vendors", one: "vendor", owed: "Payables", path: "/vendors", base: "Purchase",
            chart: "Purchases", sections: [["Purchase", "Bills"], ["Payment", "Payments made"], ["Debit Note", "Vendor credits"],
                                           ["Journal", "Journals"], ["Credit Note", "Credit notes"], ["Receipt", "Receipts"]] },
};

function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function PartyList({ kind, selected, compact }) {
  const navigate = useNavigate();
  const copy = COPY[kind];
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const query = useDebounced(q);
  useEffect(() => setPage(1), [query]);
  const state = useBooks("/books/parties", { kind, q: query, page, page_size: compact ? 100 : 50 });
  if (state.notConnected) return <NotConnected />;
  const search = (
    <div className="relative">
      <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${copy.title.toLowerCase()}`}
        className={`${compact ? "w-full" : "w-72"} pl-8`} data-testid="party-search" />
    </div>
  );
  if (compact) {
    return (
      <div className="flex h-full flex-col">
        <div className="border-b border-slate-200 p-3">{search}</div>
        <div className="flex-1 overflow-y-auto">
          {(state.data?.rows || []).map((p) => (
            <button key={p.guid} onClick={() => navigate(`${copy.path}/${p.guid}`)}
              className={`block w-full border-b border-slate-100 px-4 py-3 text-left ${p.guid === selected ? "bg-blue-50" : "hover:bg-slate-50"}`}>
              <div className="truncate text-sm font-medium text-slate-900">{p.name}</div>
              <div className="text-sm tabular-nums text-slate-500">{money(p.balance)}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-medium text-slate-500">{kind === "customer" ? "Sales" : "Purchases"}</div>
          <h1 className="text-2xl font-semibold tracking-tight">{copy.title}</h1>
          {state.data && (
            <div className="mt-0.5 text-sm text-slate-500">
              {state.data.total.toLocaleString("en-IN")} {copy.title.toLowerCase()} · total {copy.owed.toLowerCase()} {money(state.data.total_balance)}
            </div>
          )}
        </div>
        {search}
      </div>
      <Loaded state={state}>
        {state.data && (
          <>
            <DataTable
              onRowClick={(p) => navigate(`${copy.path}/${p.guid}`)}
              empty={`No ${copy.title.toLowerCase()} in Tally${q ? " match this search" : ""}.`}
              columns={[
                { key: "name", label: "Name", render: (p) => <span className="font-medium text-blue-700">{p.name}</span> },
                { key: "gstin", label: "GSTIN", render: (p) => <span className="font-mono text-xs">{p.gstin || "—"}</span> },
                { key: "phone", label: "Phone", render: (p) => p.phone || "—" },
                { key: "state", label: "State", render: (p) => p.state || "—" },
                { key: "balance", label: copy.owed, align: "right", render: (p) => money(p.balance) },
              ]}
              rows={state.data.rows} />
            <Pager page={state.data.page} pageSize={state.data.page_size} total={state.data.total} onChange={setPage} />
          </>
        )}
      </Loaded>
    </div>
  );
}

function Overview({ party }) {
  const copy = COPY[party.kind];
  return (
    <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
      <div className="space-y-5 text-sm">
        <Block title="Address">
          {party.address.length ? party.address.map((line, i) => <div key={i}>{line}</div>) : <div className="text-slate-400">No address in Tally</div>}
          {(party.state || party.pincode) && <div>{[party.state, party.pincode].filter(Boolean).join(" - ")}</div>}
          {party.country && <div>{party.country}</div>}
        </Block>
        <Block title="Other details">
          <Row label="GSTIN" value={party.gstin} mono />
          <Row label="GST registration" value={party.gst_registration_type} />
          <Row label="PAN" value={party.pan} mono />
          <Row label="Contact" value={party.contact} />
          <Row label="Phone" value={party.mobile || party.phone} />
          <Row label="Email" value={party.email} />
          <Row label="Credit period" value={party.credit_period_days ? `${party.credit_period_days} days` : null} />
          <Row label="Bill-by-bill" value={party.is_bill_wise ? "Yes" : "No"} />
          <Row label="Tally group" value={party.group} />
        </Block>
      </div>
      <div className="space-y-6">
        <div>
          <div className="mb-2 text-[15px] font-semibold">{copy.owed}</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <th className="py-2 text-left">Currency</th>
                <th className="py-2 text-right">Outstanding {copy.owed.toLowerCase()}</th>
                <th className="py-2 text-right">Unused credits</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="py-2.5">INR - Indian Rupee</td>
                <td className="py-2.5 text-right tabular-nums">{money(party.balance)}</td>
                <td className="py-2.5 text-right tabular-nums">{money(party.unused_credits)}</td>
              </tr>
            </tbody>
          </table>
          <div className="mt-1 text-xs text-slate-500">Opening balance {money(party.opening)}</div>
        </div>
        <div>
          <div className="mb-1 text-[15px] font-semibold">{copy.chart} <span className="text-xs font-normal text-slate-500">last 6 months, incl. tax</span></div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={party.chart} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} stroke="#e1e0d9" />
              <XAxis dataKey="month" tickFormatter={shortMonth} tick={{ fill: "#898781", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#c3c2b7" }} />
              <YAxis tickFormatter={compactMoney} tick={{ fill: "#898781", fontSize: 11 }} tickLine={false} axisLine={false} width={56} />
              <Tooltip cursor={{ fill: "rgba(11,11,11,0.04)" }} formatter={(v) => [money(v), copy.chart]} labelFormatter={shortMonth} />
              <Bar dataKey="amount" fill={SERIES_1} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {party.open_bills.length > 0 && (
          <div>
            <div className="mb-2 text-[15px] font-semibold">Open bills</div>
            <DataTable rowKey={(b) => `${b.bill}-${b.date}`}
              columns={[
                { key: "bill", label: "Bill", className: "whitespace-nowrap", render: (b) => (b.voucher_guid
                  ? <Link to={`/vouchers/${b.voucher_guid}`} className="text-blue-700 hover:underline">{b.bill}</Link> : b.bill) },
                { key: "date", label: "Date", className: "whitespace-nowrap", render: (b) => dateLabel(b.date) },
                { key: "due_date", label: "Due", className: "whitespace-nowrap", render: (b) => dateLabel(b.due_date) },
                { key: "days", label: "Overdue", className: "whitespace-nowrap", render: (b) => (b.days_overdue ? `${b.days_overdue} days` : "—") },
                { key: "outstanding", label: "Balance due", align: "right", render: (b) => money(b.outstanding) },
              ]}
              rows={party.open_bills} />
          </div>
        )}
      </div>
    </div>
  );
}

function Transactions({ party }) {
  const copy = COPY[party.kind];
  const state = useBooks("/books/vouchers", { party: party.guid, page_size: 200 });
  const groups = useMemo(() => {
    const by = {};
    (state.data?.rows || []).forEach((v) => { (by[v.base_type] = by[v.base_type] || []).push(v); });
    return by;
  }, [state.data]);
  return (
    <Loaded state={state}>
      <div className="space-y-3">
        {copy.sections.filter(([base], i) => i < 4 || groups[base]).map(([base, label]) => (
          <Section key={base} label={label} rows={groups[base] || []} defaultOpen={base === copy.base} />
        ))}
        {state.data?.total > 200 && <div className="text-xs text-slate-500">Showing the latest 200 vouchers.</div>}
      </div>
    </Loaded>
  );
}

function Section({ label, rows, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const navigate = useNavigate();
  return (
    <div className="rounded-lg border border-slate-200">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 px-4 py-3 text-left">
        {open ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}
        <span className="font-semibold">{label}</span>
        <span className="text-xs text-slate-500">{rows.length}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100">
          {rows.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-500">There are no {label.toLowerCase()}.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Number</th>
                  <th className="px-4 py-2 text-left">Reference</th><th className="px-4 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.guid} onClick={() => navigate(`/vouchers/${v.guid}`)} className="cursor-pointer border-b border-slate-50 last:border-0 hover:bg-blue-50/40">
                    <td className="px-4 py-2">{dateLabel(v.date)}</td>
                    <td className="px-4 py-2 text-blue-700">{v.number || "—"} {v.is_cancelled && <span className="text-xs text-rose-600">cancelled</span>}</td>
                    <td className="px-4 py-2 text-slate-600">{v.reference || ""}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{amount(v.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const Block = ({ title, children }) => (
  <div>
    <div className="mb-2 border-b border-slate-200 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</div>
    <div className="space-y-1">{children}</div>
  </div>
);

const Row = ({ label, value, mono }) => (
  <div className="flex gap-3">
    <span className="w-32 shrink-0 text-slate-500">{label}</span>
    <span className={`${mono ? "font-mono text-xs" : ""} ${value ? "text-slate-900" : "text-slate-400"}`}>{value || "—"}</span>
  </div>
);

function PartyDetail({ kind, guid }) {
  const navigate = useNavigate();
  const copy = COPY[kind];
  const [tab, setTab] = useState("overview");
  const [period, setPeriod, fyMonth] = usePeriod("this_fy");
  const state = useBooks(`/books/parties/${guid}`);
  useEffect(() => setTab("overview"), [guid]);
  return (
    <div className="min-w-0 flex-1">
      <Loaded state={state}>
        {state.data && (
          <div>
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-6 py-4">
              <div className="min-w-0">
                <h1 className="truncate text-2xl font-semibold tracking-tight">{state.data.name}</h1>
                <div className="text-sm text-slate-500">{copy.owed} {money(state.data.balance)}</div>
              </div>
              <button onClick={() => navigate(copy.path)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Close">
                <X size={20} />
              </button>
            </div>
            <div className="px-6 pt-3">
              <Tabs tabs={[["overview", "Overview"], ["transactions", "Transactions"], ["statement", "Statement"]]} value={tab} onChange={setTab} />
            </div>
            <div className="p-6">
              {tab === "overview" && <Overview party={state.data} />}
              {tab === "transactions" && <Transactions party={state.data} />}
              {tab === "statement" && (
                <div>
                  <div className="mb-4 text-center">
                    <div className="text-lg font-semibold">{copy.one === "customer" ? "Customer" : "Vendor"} statement for {state.data.name}</div>
                    <div className="text-sm text-slate-500">From {dateLabel(period.from)} to {dateLabel(period.to)}</div>
                  </div>
                  <LedgerStatement guid={guid} period={period} setPeriod={setPeriod} fyMonth={fyMonth} />
                </div>
              )}
            </div>
          </div>
        )}
      </Loaded>
    </div>
  );
}

/** /customers, /customers/:guid, /vendors, /vendors/:guid */
export default function Parties({ kind }) {
  const { guid } = useParams();
  if (!guid) return <PartyList kind={kind} />;
  return (
    <div className="-m-4 flex min-h-[calc(100vh-56px)] sm:-m-6">
      <div className="hidden w-72 shrink-0 border-r border-slate-200 lg:block">
        <PartyList kind={kind} selected={guid} compact />
      </div>
      <PartyDetail kind={kind} guid={guid} />
    </div>
  );
}
